import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { ContractFormValues, WorkFormat } from "@/features/hr/contracts/types";
import { WORK_FORMAT_CHOICES } from "@/features/hr/contracts/consts";
import { Checkbox, Input, Select, TimePicker, Toast } from "@/shared/ui";
import { WorkingDays } from "@/shared/components";
import { type Locale } from "@/shared/utils";

export default function WorkScheduleForm() {
  const { i18n, t } = useTranslation("FillContractPage");
  const locale = (i18n.language as Locale) || "ru";
  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<ContractFormValues>();

  const hasBreak = watch("has_break");
  const trialPeriod = watch("trial_period");
  const isParallelWorker = watch("is_parallel_worker");
  const workStartTime = watch("work_start_time");
  const workEndTime = watch("work_end_time");
  const breakStartTime = watch("break_start_time");
  const breakEndTime = watch("break_end_time");

  const workEndTimeError = useMemo(() => {
    if (!errors.work_end_time?.message) return undefined;
    return t(errors.work_end_time.message as string);
  }, [errors.work_end_time?.message, t]);

  function parseTimeToMinutes(time: string): number {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  }

  const breakEndBeforeStartWarning = useMemo(() => {
    if (!hasBreak || !breakStartTime || !breakEndTime) return null;
    const breakStartMinutes = parseTimeToMinutes(breakStartTime);
    const breakEndMinutes = parseTimeToMinutes(breakEndTime);
    if (breakEndMinutes <= breakStartMinutes) {
      return t("validation.break_end_before_start");
    }
    return null;
  }, [hasBreak, breakStartTime, breakEndTime, t]);

  const breakRequiredWarning = useMemo(() => {
    if (!workStartTime || !workEndTime) return null;
    const grossMinutes = parseTimeToMinutes(workEndTime) - parseTimeToMinutes(workStartTime);
    if (grossMinutes < 480) return null;
    if (!hasBreak) {
      return t("validation.break_required_for_eight_hours");
    }
    if (breakStartTime && breakEndTime) {
      const breakMinutes = parseTimeToMinutes(breakEndTime) - parseTimeToMinutes(breakStartTime);
      if (breakMinutes > 0 && breakMinutes < 30) {
        return t("validation.break_min_thirty_minutes");
      }
    }
    return null;
  }, [workStartTime, workEndTime, hasBreak, breakStartTime, breakEndTime, t]);

  const workFormatOptions = useMemo(
    () => WORK_FORMAT_CHOICES.map((c) => ({ label: t(c.labelKey), value: c.value })),
    [t],
  );

  return (
    <div className="flex flex-col gap-7">
      <h2 className="text-display-2xs content-base-primary">{t("sections.work_schedule")}</h2>

      <div className="grid grid-cols-2 gap-y-4 gap-x-3">
        <div className="col-span-2">
          <WorkingDays
            label={t("form.working_days_list")}
            value={watch("working_days_list")}
            onChange={(days) =>
              setValue("working_days_list", days, {
                shouldValidate: true,
              })
            }
            locale={locale}
            error={errors.working_days_list?.message ? t(errors.working_days_list.message as string) : undefined}
          />
        </div>

        <TimePicker
          label={t("form.work_start_time")}
          hasRequiredLabel
          placeholder={t("placeholders.work_start_time")}
          value={watch("work_start_time")}
          onChange={(value) => {
            setValue("work_start_time", value, { shouldValidate: true });
            trigger(["break_start_time", "break_end_time", "work_end_time"]);
          }}
          locale={locale}
          error={errors.work_start_time?.message ? t(errors.work_start_time.message as string) : undefined}
        />

        <TimePicker
          label={t("form.work_end_time")}
          hasRequiredLabel
          placeholder={t("placeholders.work_end_time")}
          value={watch("work_end_time")}
          onChange={(value) => {
            setValue("work_end_time", value, { shouldValidate: true });
            trigger(["break_start_time", "break_end_time", "work_start_time"]);
          }}
          locale={locale}
          error={workEndTimeError}
        />

        <div className="col-span-2">
          <Checkbox
            label={t("form.has_break")}
            state={hasBreak ? "checked" : "unchecked"}
            onChange={() => {
              const newValue = !hasBreak;
              setValue("has_break", newValue, { shouldValidate: true });
              if (!newValue) {
                setValue("break_start_time", undefined, { shouldValidate: true });
                setValue("break_end_time", undefined, { shouldValidate: true });
              }
            }}
          />
        </div>

        {hasBreak && (
          <>
            <TimePicker
              label={t("form.break_start_time")}
              placeholder={t("placeholders.break_start_time")}
              value={watch("break_start_time")}
              onChange={(value) => {
                setValue("break_start_time", value, { shouldValidate: true });
                trigger("break_end_time");
              }}
              locale={locale}
              error={errors.break_start_time?.message ? t(errors.break_start_time.message as string) : undefined}
            />

            <TimePicker
              label={t("form.break_end_time")}
              placeholder={t("placeholders.break_end_time")}
              value={watch("break_end_time")}
              onChange={(value) => {
                setValue("break_end_time", value, { shouldValidate: true });
                trigger("break_start_time");
              }}
              locale={locale}
              error={errors.break_end_time?.message ? t(errors.break_end_time.message as string) : undefined}
            />
          </>
        )}

        <Select
          label={t("form.work_format")}
          hasRequiredLabel
          placeholder={t("placeholders.work_format")}
          options={workFormatOptions}
          value={watch("work_format") ?? undefined}
          onChange={(value: WorkFormat | null) => setValue("work_format", value ?? undefined, { shouldValidate: true })}
          error={errors.work_format?.message ? t(errors.work_format.message as string) : undefined}
        />

        <Input
          label={t("form.salary_amount")}
          hasRequiredLabel
          placeholder={t("placeholders.salary_amount")}
          value={watch("salary_amount")}
          onChange={(e) => setValue("salary_amount", e.target.value, { shouldValidate: true })}
          onlyNumber
          error={errors.salary_amount?.message ? t(errors.salary_amount.message as string) : undefined}
        />

        <div className="col-span-2">
          <Checkbox
            label={t("form.is_parallel_worker")}
            state={isParallelWorker ? "checked" : "unchecked"}
            onChange={() => setValue("is_parallel_worker", !isParallelWorker, { shouldValidate: true })}
          />
        </div>

        <div className="col-span-2">
          <Checkbox
            label={t("form.trial_period")}
            state={trialPeriod ? "checked" : "unchecked"}
            onChange={() => {
              const newValue = !trialPeriod;
              setValue("trial_period", newValue, { shouldValidate: true });
              if (!newValue) {
                setValue("trial_duration_months", undefined, { shouldValidate: true });
              }
            }}
          />
        </div>

        {trialPeriod && (
          <Input
            label={t("form.trial_duration_months")}
            placeholder={t("placeholders.trial_duration_months")}
            value={watch("trial_duration_months")?.toString() || ""}
            onChange={(e) => {
              const value = e.target.value;
              const numValue = value === "" ? undefined : parseInt(value, 10);
              setValue("trial_duration_months", isNaN(numValue as number) ? undefined : numValue, {
                shouldValidate: true,
              });
            }}
            onlyNumber
            error={
              errors.trial_duration_months?.message ? t(errors.trial_duration_months.message as string) : undefined
            }
          />
        )}
      </div>

      {breakEndBeforeStartWarning && (
        <Toast
          key="break-end-before-start"
          color="negative"
          text={breakEndBeforeStartWarning}
          onClose={() => {}}
        />
      )}

      {breakRequiredWarning && !breakEndBeforeStartWarning && (
        <Toast
          key="break-required-warning"
          color="notice"
          text={breakRequiredWarning}
          onClose={() => {}}
        />
      )}
    </div>
  );
}
